import moment from 'moment'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js'
import { useCallback, useMemo } from 'react'

import globals from '../../../../../assets/styles/globals.scss'
import chartTooltipClasses from '../ChartTooltip.module.scss'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

Tooltip.positioners.cursor = function(elements, coordinates) {
  return { x: coordinates.x, y: coordinates.y }
}

const MemberGrowthChart = ({ monthlyData, chartDataType, showOnlyNew }) => {
  const currentYear = new Date().getFullYear()

  const labels = useMemo(() => monthlyData.map(item => {
    const date = moment(item.month)
    return date.year() !== currentYear ? date.format('MMM YYYY') : date.format('MMM')
  }), [currentYear, monthlyData])

  const datasets = useMemo(() => {
    const newDataset = {
      label: 'New',
      backgroundColor: globals.primaryOrange,
      hoverBackgroundColor: globals.paleOrange,
      stack: 'stack-0',
      data: monthlyData.map(month => month.new),
    }

    if (showOnlyNew) {
      return [newDataset]
    }

    return [
      {
        label: 'Existing',
        backgroundColor: globals.slateGray,
        hoverBackgroundColor: globals.slateGrayIcons,
        stack: 'stack-0',
        data: monthlyData.map(month => month.existing),
      },
      newDataset,
    ]
  }, [monthlyData, showOnlyNew])

  const externalTooltipHandler = useCallback(context => {
    let tooltipEl = document.getElementById('chartjs-tooltip-member-growth')

    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'chartjs-tooltip-member-growth'
      tooltipEl.className = chartTooltipClasses.tooltip
      document.body.appendChild(tooltipEl)
    }

    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      return
    }

    const dataIndex = tooltipModel.dataPoints[0].dataIndex
    const row = monthlyData[dataIndex]
    const date = moment.utc(row.month)
    const title = date.year() === currentYear ? date.format('MMMM') : date.format('MMMM YYYY')

    let innerHtml = `
      <div style="font-weight:800; font-size: 1rem; color:${globals.darkNavy}; margin-bottom:0.75rem;">
        ${title}
      </div>
    `

    innerHtml += `
      <div style="display:flex; justify-content:space-between; gap:1.5rem; margin-bottom:0.25rem; font-size:0.9rem;">
        <span>
          <span
            style="display:inline-block; width:8px; height:8px; border-radius:50%; background:${globals.primaryOrange}; margin-right:0.5rem;"
          ></span>
          Total new ${chartDataType}
        </span>
        <span style="font-weight:800;">${row.new}</span>
      </div>
    `

    if (!showOnlyNew) {
      innerHtml += `
        <div style="display:flex; justify-content:space-between; gap:1.5rem; font-size:0.9rem;">
          <span>
            <span
              style="display:inline-block; width:8px; height:8px; border-radius:50%; background:${globals.slateGray}; margin-right:0.5rem;"
            ></span>
            Total existing ${chartDataType}
          </span>
          <span style="font-weight:800;">${row.existing}</span>
        </div>
      `
    }

    tooltipEl.innerHTML = innerHtml

    const canvasRect = context.chart.canvas.getBoundingClientRect()
    tooltipEl.style.opacity = 1

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    let left = canvasRect.left + window.pageXOffset + tooltipModel.caretX + 12
    let top = canvasRect.top + window.pageYOffset + tooltipModel.caretY + 12

    const tooltipRect = tooltipEl.getBoundingClientRect()
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 8
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - 8
    }

    tooltipEl.style.left = `${left}px`
    tooltipEl.style.top = `${top}px`
  }, [monthlyData, currentYear, chartDataType, showOnlyNew])

  const chartData = { labels, datasets }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        position: 'cursor',
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 5,
        },
      },
    },
  }

  return (
    <div className='h-100 w-100'>
      <Bar data={chartData} options={options} />
    </div>
  )
}

MemberGrowthChart.propTypes = {
  monthlyData: PropTypes.array.isRequired,
  chartDataType: PropTypes.oneOf(['students', 'families']).isRequired,
  showOnlyNew: PropTypes.bool,
}

export default MemberGrowthChart
